import { useUserData } from '@conteg/auth';
import {
  Alert,
  Button,
  ErrorDetailInfo,
  Flex,
  Heading,
  Input,
  InputTypes,
  Loading,
  Option,
  Select,
  showAlertModalError,
  Text,
} from '@conteg/ui';
import Notification from 'components/notification/notification';
import { CUSTOM_AUTH_TOKEN } from 'config';
import { SHA256 } from 'crypto-js';
import { useCallback, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  LockStatus,
  ReservationStatus,
  ServiceType,
  StorageUnitDocument,
  TwoFactorVerificationStatus,
  useOpenStorageUnitMutation,
  useTenantsQuery,
  useVerifyAccessPinMutation,
  useVerifyTwoFactorMutation,
} from 'types/generated/graphql';
import { usePoint } from 'utils/device/device-store';
import { matchesGqlErrorCode } from 'utils/errors/match-gql-error-code';
import { useOnLockChangeHandler } from 'utils/hooks/storage-units-event-handlers';
import { useGetOtp } from 'utils/hooks/use-get-otp';
import { useInvalidateQueries } from 'utils/hooks/use-ivalidate-queries';
import { logger } from 'utils/logging/init';

import ConfirmPickupReservation from '../confirm-pickup-reservation';
import ShowOneTimePassword from '../one-time-password/show-one-time-password';
import {
  MAX_TWO_FACTOR_ATTEMPTS,
  remainingTwoFactorAttempts,
  twoFactorAttemptsAlertType,
} from '../two-factor-attempts';

type FormInputs = {
  accessPin: string;
  twoFactorCode?: string;
  tenantSubjectId?: string;
  projectId?: string;
  serviceType?: string;
};

const OpenWithPin = ({
  storageUnitId,
  reservationId,
  requiresTwoFactor: reservationRequiresTwoFactor = false,
  twoFactorFailedAttempts = 0,
  reservationStatus,
  openWithoutVerifyStep,
  tenantSubjectId,
  serviceType,
}: {
  openWithoutVerifyStep: boolean;
  storageUnitId?: string;
  reservationId?: string;
  /**
   * The parcel is still waiting for its recipient release code (UPS EPRA): the reservation's
   * is2FaRequired with no twoFactorVerifiedAt yet. Only decides whether to render the second field -
   * whether the code is actually sent comes from the gateway's answer to the pin verification.
   */
  requiresTwoFactor?: boolean;
  /** Codes already refused for this reservation, so the counter is right on the first render. */
  twoFactorFailedAttempts?: number;
  reservationStatus?: ReservationStatus | null | undefined;
  tenantSubjectId?: string;
  serviceType?: ServiceType | null | undefined;
}) => {
  const { t } = useTranslation();

  const userData = useUserData(CUSTOM_AUTH_TOKEN);
  const userId = userData?.userId;

  const { pointId } = usePoint();
  const onLockChange = useOnLockChangeHandler();

  const invalidate = useInvalidateQueries();

  const { mutateAsync: openStorageUnit, isPending: isOpening } =
    useOpenStorageUnitMutation();

  const { mutateAsync: verifyAccessPin, isPending: isVerifying } =
    useVerifyAccessPinMutation();

  const { mutateAsync: verifyTwoFactor, isPending: isVerifyingTwoFactor } =
    useVerifyTwoFactorMutation();

  const [bindingFee, setBindingFee] = useState<number>(0);
  const [formNotice, setFormNotice] = useState<string>('');
  const [storageUnitIdsResponse, setStorageUnitIdsResponse] = useState<
    Array<{ storageUnitId: string; storageUnitName?: string | null }>
  >([]);

  const [accessPin, setAccessPin] = useState<string>('');
  const [isAccessPinVerified, setAccessPinVerified] = useState<boolean>(false);
  // The reservation's flag is written when the reservation is created and never changes, so a parcel UPS
  // gates only afterwards arrives here as false. The gateway re-reads the live flag from the carrier on
  // every pin verification, so when it says a code is needed the field appears and the operator can
  // simply submit again - rather than facing a dialog that refuses to open with nothing to fill in.
  const [carrierRequiresTwoFactor, setCarrierRequiresTwoFactor] =
    useState<boolean>(false);
  const requiresTwoFactor =
    reservationRequiresTwoFactor || carrierRequiresTwoFactor;

  // The storage-unit detail knows the reservation from the start; the storage-unit list resolves it only
  // from the answer to the open attempt, and the code has to be verified against something.
  const [resolvedReservationId, setResolvedReservationId] = useState<
    string | undefined
  >(reservationId);

  // Refused codes so far, counted per reservation across every channel - attempts spent at the kiosk
  // are gone here too. Seeded from the reservation so the count is on screen before the operator asks
  // the recipient for anything, then kept current from each answer without a refetch.
  const [failedAttempts, setFailedAttempts] = useState<number>(
    twoFactorFailedAttempts
  );
  // Message under the release-code field. Kept apart from formNotice so a wrong code and a wrong pin
  // never end up reported at the same place - with three attempts before the parcel goes back to the
  // sender, the operator has to see which of the two they mistyped.
  const [twoFactorNotice, setTwoFactorNoticeState] = useState<{
    // Notification hides itself five seconds after it mounts and never returns, so a second wrong code
    // would give no feedback at all. The seq remounts it through a key, restarting that window.
    seq: number;
    messageKey: string;
    params?: Record<string, string>;
  }>({ seq: 0, messageKey: '' });

  const setTwoFactorNotice = (
    messageKey: string,
    params?: Record<string, string>
  ) =>
    setTwoFactorNoticeState((previous) => ({
      seq: previous.seq + 1,
      messageKey,
      params,
    }));
  const [showOtp, setShowOtp] = useState<boolean>(false);
  const [showOptButton, setShowOptButton] = useState<boolean>(true);
  const [showEndReservation, setShowEndReservation] = useState<boolean>(false);

  const { otp, setStartFetching } = useGetOtp(
    storageUnitId ?? '',
    reservationStatus
  );

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      accessPin: '',
      projectId: '',
      serviceType: '',
    },
  });

  const {
    data: tenants,
    isLoading,
    error: tenantsError,
  } = useTenantsQuery(
    {
      pointId: pointId as string,
    },
    {
      enabled: !!pointId,
      gcTime: 0,
      staleTime: 0,
      select: (data) => data.tenants,
    }
  );

  // This is for opening storage unit from list of storage units, when user selects tenant from select box to open storage unit
  const options: Option[] =
    tenants?.map((data) => ({
      label: data?.tenantName ?? '',
      value: data?.tenantSubjectId ?? '',
    })) ?? [];

  const watchTenantSubjectId = watch('tenantSubjectId');

  const getOpenedStorageUnits = (
    storageUnits: Array<{
      storageUnitId: string;
      storageUnitName?: string | null;
    }>
  ) =>
    storageUnits.length
      ? storageUnits
      : storageUnitId
        ? [{ storageUnitId, storageUnitName: null }]
        : [];

  useEffect(() => {
    if (watchTenantSubjectId && tenants) {
      const selectedTenant = tenants.find(
        (tenant) => tenant?.tenantSubjectId === watchTenantSubjectId
      );

      if (selectedTenant) {
        setValue('projectId', selectedTenant.projectId);
        setValue('serviceType', selectedTenant.tenantServiceType);
      }
    }
  }, [watchTenantSubjectId, setValue, tenants]);

  const remainingAttempts = remainingTwoFactorAttempts(failedAttempts);

  const attemptsMessageKey =
    remainingAttempts === 0
      ? 'Page.StorageUnit.Actions.TwoFactor.Attempts.Error'
      : remainingAttempts === 1
        ? 'Page.StorageUnit.Actions.TwoFactor.Attempts.Warning'
        : 'Page.StorageUnit.Actions.TwoFactor.Attempts';

  // Shared by both forms in this component: the one on the storage-unit detail, which knows up front
  // that the parcel needs two-factor verification, and the one on the storage-unit list, which finds out only from the
  // open answer. Kept as one value so the two cannot drift apart.
  const twoFactorFields = requiresTwoFactor ? (
    <>
      {/* Alert, not Notification: this must stay on screen. Notification hides itself after a few
          seconds, and a warning about irreversibly returning a parcel is exactly the kind that has to
          be readable when the operator finally types. */}
      <Alert
        testId="two-factor-warning"
        type="warning"
        message={t('Page.StorageUnit.Actions.TwoFactor.Warning', {
          maxAttempts: String(MAX_TWO_FACTOR_ATTEMPTS),
        })}
      />
      <Text
        variant="large"
        content={t('Page.StorageUnit.Actions.TwoFactor.Text')}
      />
      <Alert
        testId="two-factor-attempts"
        // The shared Alert offers no neutral variant, hence success while there is room.
        type={twoFactorAttemptsAlertType(remainingAttempts)}
        message={t(attemptsMessageKey, {
          failedAttempts: String(failedAttempts),
          remainingAttempts: String(remainingAttempts),
          maxAttempts: String(MAX_TWO_FACTOR_ATTEMPTS),
        })}
      />
      {twoFactorNotice.messageKey && (
        <Notification
          key={twoFactorNotice.seq}
          testId="open-with-two-factor"
          message={twoFactorNotice.messageKey}
          translationParams={twoFactorNotice.params}
        />
      )}
      <Input
        testId="open-with-pin-two-factor"
        // Deliberately not masked, unlike the pin and unlike the kiosk: the operator is reading this
        // code back from a phone call with the recipient, so masking would only hide their own typo.
        type={InputTypes.TEXT}
        label={t('Page.StorageUnit.Actions.TwoFactor.Code')}
        placeholder={t('Page.StorageUnit.Actions.TwoFactor.Code')}
        error={errors.twoFactorCode?.message && t(errors.twoFactorCode.message)}
        {...register('twoFactorCode', { required: 'Error.Form.Required' })}
      />
    </>
  ) : null;

  const getTranslatedErrorLabels = () => ({
    hideDetailsText: t('Error.HideDetailsText'),
    copyErrorText: t('Error.CopyErrorText'),
    displayErrorSummaryText: t('Error.DisplayErrorSummaryText'),
    displayErrorDetailsText: t('Error.DisplayErrorDetailsText'),
  });

  /**
   * Verifies the access pin and, for a two-factor parcel, the recipient's release code - one button, but two
   * calls in order. The code is only sent once the pin has verified: sending both blindly would let a
   * mistyped pin spend one of the recipient's three code attempts, and the third failure returns the
   * parcel to the sender.
   */
  const handleVerifyCodes: SubmitHandler<FormInputs> = async ({
    accessPin,
    twoFactorCode,
  }) => {
    setFormNotice('');
    setTwoFactorNotice('');

    const verifyAccessPinData = {
      accessPin,
      pointId: pointId as string,
      tenantSubjectId: tenantSubjectId as string,
      storageUnitId: storageUnitId as string,
      projectId: tenants?.[0]?.projectId as string,
      serviceType: serviceType as ServiceType,
    };

    const loggableVariables = JSON.stringify({
      ...verifyAccessPinData,
      accessPin: SHA256(accessPin).toString(),
    });

    let pinResult;
    try {
      pinResult = await verifyAccessPin(verifyAccessPinData);
    } catch (error) {
      showAlertModalError(t('Page.StorageUnit.Actions.Open.Pin.Error'), error);
      setAccessPin('');
      setAccessPinVerified(false);
      logger.error(
        `Calling mutation verifyAccessPin resolved in error with variables ${loggableVariables}`
      );
      return;
    }

    if (pinResult.verifyAccessPin.bindingFee) {
      setFormNotice('Page.StorageUnit.Actions.Open.BindingFee.Error');
      setBindingFee(pinResult.verifyAccessPin.bindingFee);
      logger.info(
        `Calling mutation verifyAccessPin with variables ${loggableVariables}`
      );
      reset();
      return;
    }

    // The server's answer decides whether the code is actually needed; the reservation flag only
    // decided whether to show the field. When they disagree the reservation on screen is stale, and
    // opening without the code is exactly what this ticket forbids - so say so instead.
    if (pinResult.verifyAccessPin.requiresTwoFactor) {
      // Reveal the field when only the carrier knew the parcel is gated, so submitting again is all it
      // takes. Without a reservation id there is nothing to verify against, which is a defect rather
      // than something the operator can act on.
      setCarrierRequiresTwoFactor(!!resolvedReservationId);
      setFailedAttempts(pinResult.verifyAccessPin.twoFactorFailedAttempts);

      if (!resolvedReservationId || !twoFactorCode) {
        // A missing code is on the operator; a missing reservation is on us - retyping the code would
        // hit this same guard, so it must not read as "enter the code".
        setTwoFactorNotice(
          resolvedReservationId
            ? 'Page.StorageUnit.Actions.TwoFactor.NotVerified.Error'
            : 'Page.StorageUnit.Actions.TwoFactor.Unavailable.Warning'
        );
        return;
      }

      const verified = await verifyRecipientCode(twoFactorCode);
      if (!verified) return;
    }

    setFormNotice('Page.StorageUnit.Actions.AccessPin.Success');
    setAccessPin(accessPin);
    setAccessPinVerified(true);
    reset();
  };

  /** Returns true only when the carrier accepted the code. */
  const verifyRecipientCode = async (code: string): Promise<boolean> => {
    try {
      const { verifyTwoFactor: outcome } = await verifyTwoFactor({
        reservationId: resolvedReservationId as string,
        pointId: pointId as string,
        code,
      });

      switch (outcome.status) {
        case TwoFactorVerificationStatus.Verified:
          return true;
        case TwoFactorVerificationStatus.Invalid:
          setFailedAttempts(outcome.twoFactorFailedAttempts);
          setTwoFactorNotice(
            'Page.StorageUnit.Actions.TwoFactor.Invalid.Error',
            { remainingAttempts: String(outcome.remainingAttempts) }
          );
          return false;
        case TwoFactorVerificationStatus.ReturnedToSender:
          setFailedAttempts(outcome.twoFactorFailedAttempts);
          setTwoFactorNotice(
            'Page.StorageUnit.Actions.TwoFactor.ReturnedToSender.Error'
          );
          return false;
        default:
          // Unknown status (a schema addition): never read it as an accepted code.
          logger.error(
            `Unexpected two-factor verification status: ${outcome.status}`
          );
          setTwoFactorNotice('Page.StorageUnit.Actions.TwoFactor.Error');
          return false;
      }
    } catch (error) {
      // A carrier/config failure is not a wrong code - the gateway leaves the attempt counter alone,
      // so the operator can retry straight away and needs to be told exactly that.
      setTwoFactorNotice(
        matchesGqlErrorCode(error, 'TwoFactorVerificationUnavailable')
          ? 'Page.StorageUnit.Actions.TwoFactor.Unavailable.Warning'
          : 'Page.StorageUnit.Actions.TwoFactor.Error'
      );
      return false;
    }
  };

  const handleOpenStorageUnitWithoutVerifyStep: SubmitHandler<
    FormInputs
  > = async ({
    accessPin,
    projectId,
    serviceType,
    tenantSubjectId,
    twoFactorCode,
  }) => {
    setFormNotice('');
    setTwoFactorNotice('');

    if (!pointId || !tenantSubjectId || !projectId || !serviceType) {
      throw new Error(
        `Missing required data: ${JSON.stringify({ pointId, tenantSubjectId, projectId, serviceType })}`
      );
    }

    // This screen has no reservation before the pin is verified, so the first submit can only be told
    // that a code is needed - the code field appears and this branch handles the submit after it. As on
    // the detail screen, the code is verified before opening is attempted again, so a mistyped pin can
    // never spend one of the recipient's three attempts.
    if (requiresTwoFactor) {
      if (!resolvedReservationId || !twoFactorCode) {
        // A missing code is on the operator; a missing reservation is on us - retyping the code would
        // hit this same guard, so it must not read as "enter the code".
        setTwoFactorNotice(
          resolvedReservationId
            ? 'Page.StorageUnit.Actions.TwoFactor.NotVerified.Error'
            : 'Page.StorageUnit.Actions.TwoFactor.Unavailable.Warning'
        );
        return;
      }

      const verified = await verifyRecipientCode(twoFactorCode);
      if (!verified) return;
    }

    const endUserData = {
      pointId,
      userId: userId as string,
      storageUnitId,
      tenantSubjectId,
      endUser: { accessPin },
      isBlockingReservation: false,
      projectId,
      serviceType: serviceType as ServiceType,
      singleTenantVerification: true,
    };

    await openStorageUnit(endUserData, {
      onSuccess: ({ openStorageUnits }) => {
        const openedStorageUnits = getOpenedStorageUnits(
          openStorageUnits.storageUnits
        );

        // Nothing was opened: the parcel needs two-factor verification. This answer is also the only place this screen
        // learns which reservation to verify the code against, so keep it and show the field.
        if (openStorageUnits.requiresTwoFactor) {
          // No reservation in the answer means there is nothing to verify the code against, and the
          // submit would keep failing on the same guard - so leave the field hidden and report it as a
          // verification we cannot run rather than as a code the operator has yet to enter.
          const reservationToVerify =
            openStorageUnits.reservationId ?? undefined;

          setResolvedReservationId(reservationToVerify);
          setCarrierRequiresTwoFactor(!!reservationToVerify);
          setFailedAttempts(openStorageUnits.twoFactorFailedAttempts);
          setShowEndReservation(false);
          setTwoFactorNotice(
            reservationToVerify
              ? 'Page.StorageUnit.Actions.TwoFactor.NotVerified.Error'
              : 'Page.StorageUnit.Actions.TwoFactor.Unavailable.Warning'
          );
          return;
        }

        if (openStorageUnits.bindingFee) {
          setFormNotice('Page.StorageUnit.Actions.Open.BindingFee.Error');
          setBindingFee(openStorageUnits.bindingFee);
          setShowEndReservation(false);
        } else {
          setFormNotice(
            openedStorageUnits.length > 1
              ? 'Page.StorageUnit.Actions.Open.Success.Plural'
              : 'Page.StorageUnit.Actions.Open.Success'
          );
          setShowEndReservation(true);
          setStorageUnitIdsResponse(openedStorageUnits);

          invalidate([StorageUnitDocument]);

          if (onLockChange)
            openedStorageUnits.forEach((unit) => {
              onLockChange(unit.storageUnitId, LockStatus.Opened);
            });
        }

        reset();
      },
      onError: (error) => {
        showAlertModalError(
          t('Page.StorageUnit.Actions.Open.Pin.Error'),
          error,
          getTranslatedErrorLabels()
        );
      },
    });
  };

  const handleOpenStorageUnit = async () => {
    setFormNotice('');

    const projectId = tenants?.[0]?.projectId;

    if (!pointId || !tenantSubjectId || !projectId || !serviceType) {
      throw new Error(
        `Missing required data: ${JSON.stringify({ pointId, tenantSubjectId, projectId, serviceType })}`
      );
    }

    const openStorageUnitData = {
      pointId,
      userId: userId as string,
      tenantSubjectId,
      endUser: { accessPin },
      isBlockingReservation: false,
      projectId,
      serviceType,
      singleTenantVerification: true,
    };

    await openStorageUnit(openStorageUnitData, {
      onSuccess: ({ openStorageUnits }) => {
        const openedStorageUnits = getOpenedStorageUnits(
          openStorageUnits.storageUnits
        );

        // Defensive: the code was already verified before this button appeared, so a gate here means
        // the verification was cleared or the carrier changed its mind in between. Report it rather
        // than let the screen claim a compartment opened.
        if (openStorageUnits.requiresTwoFactor) {
          setCarrierRequiresTwoFactor(true);
          setFailedAttempts(openStorageUnits.twoFactorFailedAttempts);
          setAccessPinVerified(false);
          setShowEndReservation(false);
          setTwoFactorNotice(
            'Page.StorageUnit.Actions.TwoFactor.NotVerified.Error'
          );
          return;
        }

        if (openStorageUnits.bindingFee) {
          setFormNotice('Page.StorageUnit.Actions.Open.BindingFee.Error');
          setBindingFee(openStorageUnits.bindingFee);
          setShowEndReservation(false);
          logger.info(
            `Calling mutation verifyAccessPin resulting with bindingFee and variables ${JSON.stringify(
              {
                ...openStorageUnitData,
                accessPin: SHA256(accessPin).toString(),
              }
            )}`
          );
        } else {
          setFormNotice(
            openedStorageUnits.length > 1
              ? 'Page.StorageUnit.Actions.Open.Success.Plural'
              : 'Page.StorageUnit.Actions.Open.Success'
          );
          setStorageUnitIdsResponse(openedStorageUnits);
          setShowEndReservation(true);

          invalidate([StorageUnitDocument]);

          logger.info(
            `Calling mutation verifyAccessPin with variables ${JSON.stringify({
              ...openStorageUnitData,
              accessPin: SHA256(accessPin).toString(),
            })}`
          );
          if (onLockChange)
            openedStorageUnits.forEach((unit) => {
              onLockChange(unit.storageUnitId, LockStatus.Opened);
            });
        }

        reset();
      },
      onError: () => {
        setFormNotice('Page.StorageUnit.Actions.Open.Pin.Error');
        logger.error(
          `Calling mutation verifyAccessPin resolved in error with variables ${JSON.stringify(
            {
              ...openStorageUnitData,
              accessPin: SHA256(accessPin).toString(),
            }
          )}`
        );
      },
    });
  };

  const handleClickShowOtp = useCallback(() => {
    setStartFetching(true);
    setShowOtp(true);
    setShowOptButton(false);
  }, [setStartFetching, setShowOtp, setShowOptButton]);

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center">
        <Loading text={t('Loading')} />
      </Flex>
    );
  }

  if (tenantsError) {
    return (
      <ErrorDetailInfo
        error={tenantsError}
        title={t('Error.TenantsLoadError')}
      />
    );
  }

  return (
    <>
      {openWithoutVerifyStep ? (
        <>
          <form onSubmit={handleSubmit(handleOpenStorageUnitWithoutVerifyStep)}>
            <Flex
              flexDirection="column"
              gap="6rem"
              alignItems="center"
              mt="6rem"
            >
              {formNotice && (
                <Notification
                  testId="open-with-pin"
                  message={formNotice}
                  translationParams={
                    bindingFee
                      ? { fee: bindingFee.toString() }
                      : storageUnitIdsResponse.length > 1
                        ? { count: storageUnitIdsResponse.length.toString() }
                        : undefined
                  }
                />
              )}
              {storageUnitIdsResponse.length > 1 &&
                formNotice ===
                  'Page.StorageUnit.Actions.Open.Success.Plural' && (
                  <Flex
                    flexDirection="column"
                    gap="1rem"
                    alignItems="flex-start"
                  >
                    <Text
                      variant="large"
                      content={t('Page.StorageUnit.Actions.Open.OpenedUnits')}
                    />
                    <Flex flexDirection="column" gap="0.5rem">
                      {storageUnitIdsResponse.map((unit) => (
                        <Text
                          key={unit.storageUnitId}
                          variant="large"
                          content={`• ${unit.storageUnitName || unit.storageUnitId}`}
                        />
                      ))}
                    </Flex>
                  </Flex>
                )}
              <Controller
                control={control}
                name="tenantSubjectId"
                render={({ field: { value, onChange } }) => (
                  <Select
                    hasAutocomplete={false}
                    width="100%"
                    testId="select-tenantSubjectId"
                    defaultSingleValue={value}
                    userSingleValue={value}
                    label={t(
                      'Page.StorageUnit.Actions.BlockingReservation.Select.Tenant'
                    )}
                    onSingleValueChange={onChange}
                    options={options}
                    error={
                      errors.tenantSubjectId?.message &&
                      t(errors.tenantSubjectId.message)
                    }
                    disabled={false}
                    isNullable={false}
                  />
                )}
              />
              <Input
                testId="open-with-pin-pin"
                type={InputTypes.PASSWORD}
                label={t('Page.StorageUnit.Actions.Open.Pin')}
                placeholder={t('Page.StorageUnit.Actions.Open.Pin')}
                error={errors.accessPin?.message && t(errors.accessPin.message)}
                {...register('accessPin', { required: 'Error.Form.Required' })}
              />
              {twoFactorFields}
              <Button
                testId="open-with-pin-button"
                variant="primary"
                size="xl"
                title={t(
                  requiresTwoFactor
                    ? 'Page.StorageUnit.Actions.TwoFactor.Submit'
                    : 'Page.StorageUnit.Actions.Open.Reservation'
                )}
                disabled={isOpening || isVerifyingTwoFactor}
                type="submit"
              />
            </Flex>
          </form>
          {showEndReservation && storageUnitIdsResponse.length > 0 ? (
            <Flex justifyContent="center" mt="4rem">
              <ConfirmPickupReservation
                storageUnitId={storageUnitIdsResponse[0].storageUnitId}
                storageUnitCount={storageUnitIdsResponse.length}
                askImmediately
              />
            </Flex>
          ) : null}
        </>
      ) : (
        <>
          {isAccessPinVerified ? (
            <Flex flexDirection="column" gap="5rem">
              {formNotice && (
                <Notification
                  testId="open-with-pin"
                  message={formNotice}
                  translationParams={
                    bindingFee
                      ? { fee: bindingFee.toString() }
                      : storageUnitIdsResponse.length > 1
                        ? { count: storageUnitIdsResponse.length.toString() }
                        : undefined
                  }
                />
              )}
              {storageUnitIdsResponse.length > 1 &&
                formNotice ===
                  'Page.StorageUnit.Actions.Open.Success.Plural' && (
                  <Flex
                    flexDirection="column"
                    gap="1rem"
                    alignItems="flex-start"
                  >
                    <Text
                      variant="large"
                      content={t('Page.StorageUnit.Actions.Open.OpenedUnits')}
                    />
                    <Flex flexDirection="column" gap="0.5rem">
                      {storageUnitIdsResponse.map((unit) => (
                        <Text
                          key={unit.storageUnitId}
                          variant="large"
                          content={`• ${unit.storageUnitName || unit.storageUnitId}`}
                        />
                      ))}
                    </Flex>
                  </Flex>
                )}
              <Flex alignItems="center" gap="5rem" flexWrap="wrap">
                <Flex mt="auto">
                  <Button
                    title={t('Page.StorageUnit.Actions.Open.Reservation')}
                    testId="open-with-pin-button"
                    onClick={handleOpenStorageUnit}
                    variant="primary"
                    size="xl"
                    disabled={isOpening}
                  />
                </Flex>
                {showEndReservation && storageUnitIdsResponse.length > 0 ? (
                  <Flex justifyContent="center">
                    <ConfirmPickupReservation
                      storageUnitId={storageUnitIdsResponse[0].storageUnitId}
                      storageUnitCount={storageUnitIdsResponse.length}
                      askImmediately
                    />
                  </Flex>
                ) : null}
                {showOptButton && (
                  <Flex mt="4rem">
                    <Button
                      title={t('Page.StorageUnit.Otp.Button')}
                      testId="otp-button"
                      onClick={handleClickShowOtp}
                      variant="primary"
                      size="xl"
                    />
                  </Flex>
                )}
                {showOtp && storageUnitIdsResponse.length > 0 ? (
                  <ShowOneTimePassword
                    storageUnitId={storageUnitIdsResponse[0].storageUnitId}
                    storageUnitCount={storageUnitIdsResponse.length}
                    oneTimePassword={otp}
                  />
                ) : null}
              </Flex>
            </Flex>
          ) : (
            <form onSubmit={handleSubmit(handleVerifyCodes)}>
              <Flex
                flexDirection="column"
                gap="6rem"
                alignItems="center"
                mt="6rem"
              >
                {formNotice && (
                  <Notification
                    testId="open-with-pin"
                    message={formNotice}
                    translationParams={
                      bindingFee
                        ? { fee: bindingFee.toString() }
                        : storageUnitIdsResponse.length > 1
                          ? { count: storageUnitIdsResponse.length.toString() }
                          : undefined
                    }
                  />
                )}
                {storageUnitIdsResponse.length > 1 &&
                  formNotice ===
                    'Page.StorageUnit.Actions.Open.Success.Plural' && (
                    <Flex
                      flexDirection="column"
                      gap="1rem"
                      alignItems="flex-start"
                    >
                      <Text
                        variant="large"
                        content={t('Page.StorageUnit.Actions.Open.OpenedUnits')}
                      />
                      <Flex flexDirection="column" gap="0.5rem">
                        {storageUnitIdsResponse.map((unit) => (
                          <Text
                            key={unit.storageUnitId}
                            variant="large"
                            content={`• ${unit.storageUnitName || unit.storageUnitId}`}
                          />
                        ))}
                      </Flex>
                    </Flex>
                  )}
                <Heading
                  variant="h2"
                  title={t(
                    requiresTwoFactor
                      ? 'Page.StorageUnit.Actions.TwoFactor.Heading'
                      : 'Page.StorageUnit.Actions.Open.Verify.Heading'
                  )}
                />
                <Input
                  testId="open-with-pin-pin"
                  type={InputTypes.PASSWORD}
                  label={t('Page.StorageUnit.Actions.Open.Pin')}
                  placeholder={t('Page.StorageUnit.Actions.Open.Pin')}
                  error={
                    errors.accessPin?.message && t(errors.accessPin.message)
                  }
                  {...register('accessPin', {
                    required: 'Error.Form.Required',
                  })}
                />
                {twoFactorFields}
                <Button
                  testId="verify-access-pin-button"
                  variant="primary"
                  size="xl"
                  title={t(
                    requiresTwoFactor
                      ? 'Page.StorageUnit.Actions.TwoFactor.Submit'
                      : 'Page.StorageUnit.Actions.VerifyAccessPin'
                  )}
                  disabled={isVerifying || isVerifyingTwoFactor}
                  type="submit"
                />
              </Flex>
            </form>
          )}
        </>
      )}
    </>
  );
};

export default OpenWithPin;
